import {
  buildCopyPayloadFromRecord,
  copySourceScopeFromRecord,
  type ExplorerFileRecordCopyPayload,
} from "@/dnd/payloads";
import type { FileRecord } from "@cohesive/api-client";

/** File resource as registered for rail multi-select / multi-drag. */
export type FilesRailDragFileResource = {
  kind: "file";
  data: FileRecord;
  sharedSource?: boolean;
};

export type FilesRailDragEntry = {
  record: FileRecord;
  sharedSource?: boolean;
};

/**
 * Drag payload for files-rail rows. Source class + scope follow the record so
 * directly-shared canvas / flow-session files (now draggable from "Shared with
 * me") carry valid metadata; workspace files resolve to their own workspace
 * (falling back to the provided id).
 */
export function buildFilesRailDragPayload(
  record: FileRecord,
  workspaceId: string,
  options?: { sharedSource?: boolean },
): ExplorerFileRecordCopyPayload {
  return {
    ...buildCopyPayloadFromRecord(
      record,
      copySourceScopeFromRecord(record, workspaceId),
    ),
    // Shared-with-me files copy (never move) when dropped into a workspace.
    ...(options?.sharedSource ? { sharedSource: true } : {}),
  };
}

/**
 * Resolve which file rows participate in a files-rail drag.
 *
 * Multi-select must preserve each selected file's own `sharedSource` — never
 * broadcast the dragged row's flag onto every payload (that would mark a mixed
 * shared + owned selection as all-copy or all-move).
 */
export function resolveFilesRailDragEntries(params: {
  selectedResources: Array<{ kind: string } | FilesRailDragFileResource>;
  draggedIsSelected: boolean;
  dragged: FilesRailDragEntry;
}): FilesRailDragEntry[] {
  const selectedFiles = params.selectedResources.filter(
    (r): r is FilesRailDragFileResource => r.kind === "file",
  );
  const inMulti = params.draggedIsSelected && selectedFiles.length > 1;
  const allFiles =
    inMulti && params.selectedResources.every((r) => r.kind === "file");
  if (!allFiles) return [params.dragged];
  return selectedFiles.map((r) => ({
    record: r.data,
    sharedSource: r.sharedSource,
  }));
}
